import React, { useState, FormEvent } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { EmailRequest, SmsRequest, WhatsappRequest, SurveyEmailRequest } from "../../features/notification/NotificationSlice";
import { clearEmailNotification, clearSmsNotification, clearWhatsappNotification, clearSurveyEmailNotification, clearAllNotifications, sendEmailNotification, sendSmsNotification, sendWhatsappNotification, sendSurveyEmailNotification } from "../../features/notification/NotificationSlice";
import { AppDispatch, RootState } from "../../store/store";
import { Loader2, AlertCircle, Download, CheckCircle, XCircle } from "lucide-react";
import * as XLSX from 'xlsx';

const NOTIFICATION_TYPES = {
  email: 'Email',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  "survey-email": 'Survey Email',
} as const;

type NotificationType = keyof typeof NOTIFICATION_TYPES;

interface NotificationResult {
  code: string;
  type: NotificationType;
  status: 'success' | 'error';
  message: string;
}

interface NotificationRequest {
  voucherId: string;
  receiverList?: string[];
}

const NOTIFICATION_DISPATCHERS = {
  email: (dispatch: AppDispatch,  payload: EmailRequest) => dispatch(sendEmailNotification(payload)),
  sms: (dispatch: AppDispatch,  payload: SmsRequest) => dispatch(sendSmsNotification(payload)),
  whatsapp: (dispatch: AppDispatch,  payload: WhatsappRequest) => dispatch(sendWhatsappNotification(payload)),
  "survey-email": (dispatch: AppDispatch,  payload: SurveyEmailRequest) => dispatch(sendSurveyEmailNotification(payload)),
} as const;

const Notification: React.FC = () => {
  const dispatch = useAppDispatch();
  const { 
    emailResponse, emailError, emailLoading,
    smsResponse, smsError, smsLoading,
    whatsappResponse, whatsappError, whatsappLoading,
    surveyEmailResponse, surveyEmailError, surveyEmailLoading
  } = useAppSelector((state: RootState) => state.root.notification);

  const responses = [emailResponse, smsResponse, whatsappResponse, surveyEmailResponse];
  const errors = [emailError, smsError, whatsappError, surveyEmailError];
  const loading = [emailLoading, smsLoading, whatsappLoading, surveyEmailLoading];

  const isAnyNotificationSuccess = responses.some(r => r?.success);
  const isAnyNotificationError = errors.some(e => !!e);
  const isAnyNotificationLoading = loading.some(l => l);

  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [notificationType, setNotificationType] = useState<NotificationType>('email');
  const [notificationResults, setNotificationResults] = useState<NotificationResult[]>([]);
  const [formErrors, setFormErrors] = useState({
    excelFileError: "",
    notificationTypeError: "",
  });

  // No templates needed for either email or SMS

  // Handle Excel file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setExcelFile(file || null);
  };

  // Clear form errors and notification responses when form changes
  const handleFormChange = () => {
    setFormErrors({
      excelFileError: "",
      notificationTypeError: "",
    });
    dispatch(clearAllNotifications());
    setNotificationResults([]);
  };

  // Email validation helper function
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Phone number validation helper function - bare 10-digit India format
  // (legacy data), or a "+"-prefixed international number (e.g. Malaysia).
  const isValidPhoneNumber = (phone: string): boolean => {
    const phoneRegex = /^(\+\d{8,15}|\d{10})$/;
    return phoneRegex.test(phone);
  };

  // Process Excel file and extract data
  const processExcelFile = (file: File): Promise<{ code: string; beneficiaryEmail?: string; beneficiaryPhone?: string }[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          const processedData = jsonData.map((row: any) => {
            const beneficiaryEmail = row.beneficiaryEmail || row.BeneficiaryEmail || row.BENEFICIARYEMAIL || "";
            const beneficiaryPhone = row.beneficiaryPhone || row.BeneficiaryPhone || row.BENEFICIARYPHONE || "";
            
            return {
              code: row.code || row.Code || row.CODE || "",
              beneficiaryEmail: beneficiaryEmail && isValidEmail(beneficiaryEmail.trim()) ? beneficiaryEmail.trim() : undefined,
              beneficiaryPhone: beneficiaryPhone && isValidPhoneNumber(beneficiaryPhone.toString().trim()) ? beneficiaryPhone.toString().trim() : undefined
            };
          });

          resolve(processedData);
        } catch (error) {
          reject(new Error("Failed to process Excel file"));
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsArrayBuffer(file);
    });
  };

  // Download results as Excel
  const downloadResults = () => {
    if (notificationResults.length === 0) return;

    const ws = XLSX.utils.json_to_sheet(notificationResults);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `${notificationType.toUpperCase()} Results`);
    XLSX.writeFile(wb, `${notificationType}_notification_results.xlsx`);
  };

  // Handle form submission
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Reset error states
    setFormErrors({
      excelFileError: "",
      notificationTypeError: "",
    });

    dispatch(clearAllNotifications());
    setNotificationResults([]);

    // Validate Excel file
    if (!excelFile) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        excelFileError: "Please upload an Excel file.",
      }));
      return;
    }

    try {
      // Process Excel file
      const excelData = await processExcelFile(excelFile);

      // Handle empty Excel data
      if (excelData.length === 0) {
        setNotificationResults([{
          code: 'N/A',
          type: notificationType,
          status: 'error',
          message: 'Excel file is empty or contains no valid data'
        }]);
        return;
      }

      const results: NotificationResult[] = [];

      // Send notifications for each row in Excel
      for (const row of excelData) {
        if (row.code) {
          try {
              const request: NotificationRequest = { // In the future, introduce a `NOTIFICATION_PAYLOAD_CREATORS` utility to generate type-specific payloads from `row`.
                voucherId: row.code,
                receiverList: []
              };

              if(notificationType === 'sms' && row.beneficiaryPhone) {
                request.receiverList = [row.beneficiaryPhone];
              } else if(notificationType === 'email' && row.beneficiaryEmail) {
                request.receiverList = [row.beneficiaryEmail];
              }

              const result = await NOTIFICATION_DISPATCHERS[notificationType](dispatch, request);
              const isSuccess = result.meta.requestStatus === 'fulfilled';

              results.push({
                code: row.code,
                type: notificationType,
                status: isSuccess ? 'success' : 'error',
                message: isSuccess ? `${NOTIFICATION_TYPES[notificationType]} sent successfully` : (result.payload as string) || `Failed to send ${NOTIFICATION_TYPES[notificationType]}`
              });
            } catch (error) {
              results.push({
                code: row.code,
                type: notificationType,
                status: 'error',
                message: `Failed to send ${NOTIFICATION_TYPES[notificationType]}`
              });
            }
          } else {
          results.push({
            code: row.code || 'N/A',
            type: notificationType,
            status: 'error',
            message: 'Missing required data (code)'
          });
        }
      }

      setNotificationResults(results);
    } catch (error) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        excelFileError: "Failed to process Excel file. Please check the format.",
      }));
    }
  };

  return (
    <div className="bg-white h-full">
      {/* Header */}
      <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-7 py-8">
        <div className="flex items-center h-10">
          <h2 className="text-2xl font-bold text-gray-900">
            Notifications
          </h2>
        </div>
      </div>

      {/* Form Section */}
      <div className="max-w-7xl mx-auto px-8 sm:px-6 lg:px-8 mb-8">
        <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder mb-8">
          <form className="w-full" onChange={handleFormChange} onSubmit={handleSubmit}>
            {/* Form Fields Row */}
            <div className="flex justify-between md:flex-row gap-6 mb-6">
              {/* Notification Type Selection */}
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notification Type
                </label>
                <div className="relative">
                  <select
                    value={notificationType}
                    onChange={(e) => {
                      setNotificationType(e.target.value as NotificationType);
                    }}
                    className={`block w-full px-3 py-2 border ${formErrors.notificationTypeError ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none`}
                  >
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="survey-email">Survey Email</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {formErrors.notificationTypeError && (
                    <div className="text-red-500 text-sm mt-1 flex items-center">
                      <AlertCircle className="mr-1" />
                      {formErrors.notificationTypeError}
                    </div>
                  )}
                </div>
              </div>

              {/* File Upload */}
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Excel File Upload ({NOTIFICATION_TYPES[notificationType]})
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-brand file:text-white hover:file:bg-brand/80"
                  />
                  {formErrors.excelFileError && (
                    <div className="text-red-500 text-sm mt-1 flex items-center">
                      <AlertCircle className="mr-1" />
                      {formErrors.excelFileError}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center">
              <div className="flex gap-4">
                <button
                  className="px-4 py-2 text-sm font-medium text-white rounded-md bg-brand hover:bg-brand/80 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  type="submit"
                  disabled={isAnyNotificationLoading}
                >
                  {(isAnyNotificationLoading) ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending {NOTIFICATION_TYPES[notificationType]}...
                    </div>
                  ) : (
                    `Send ${NOTIFICATION_TYPES[notificationType]} Notifications`
                  )}
                </button>

                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    // Create and download template with headers based on notification type
                    const templateData = notificationType === 'sms' 
                      ? [{ code: "", beneficiaryPhone: "" }]
                      : [{ code: "", beneficiaryEmail: "" }];

                    const ws = XLSX.utils.json_to_sheet(templateData);
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, "Template");
                    XLSX.writeFile(wb, `${notificationType}_notification_template.xlsx`);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-1 focus:ring-green-600 focus:border-green-600 flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download Template
                </a>
              </div>
            </div>
          </form>
        </div>

        {/* Loading State */}
        {(isAnyNotificationLoading) && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder mb-8">
            <div className="flex justify-center items-center gap-2">
              <Loader2 className="w-10 h-10 animate-spin text-brand/80" />
              <span className="text-sm text-gray-500">Sending {NOTIFICATION_TYPES[notificationType]} notifications...</span>
            </div>
          </div>
        )}


        {/* Success State */}
        {(isAnyNotificationSuccess) || notificationResults.length > 0 ? (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder mb-8">


            {/* Results Table */}
            {notificationResults.length > 0 && (
              <div >
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-semibold text-gray-900">{NOTIFICATION_TYPES[notificationType]} Results Summary</h4>
                  <button
                    onClick={downloadResults}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-1 focus:ring-green-600 focus:border-green-600 flex items-center gap-2"
                  >
                    <Download className="w-3 h-3" />
                    Download Results
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Code
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Message
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {notificationResults.map((result, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {result.status === 'success' ? (
                                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                              ) : (
                                <XCircle className="w-4 h-4 text-red-600 mr-2" />
                              )}
                              <span className={`text-sm font-medium ${result.status === 'success' ? 'text-green-800' : 'text-red-800'
                                }`}>
                                {result.status === 'success' ? 'Success' : 'Error'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {result.code}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <div className="max-w-xs truncate" title={result.message}>
                              {result.message}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Summary Stats */}
                <div className="mt-4 flex gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-green-700">
                      Success: {notificationResults.filter(r => r.status === 'success').length}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <XCircle className="w-4 h-4 text-red-600" />
                    <span className="text-red-700">
                      Errors: {notificationResults.filter(r => r.status === 'error').length}
                    </span>
                  </div>
                  <div className="text-gray-600">
                    Total: {notificationResults.length}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : null}

        {/* Instructions */}
        <div className="bg-blue-50 rounded-xl p-6 border-[1px] border-blue-200">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">Instructions</h3>
          <div className="text-sm text-blue-800 space-y-2">
            <p>• Select notification type: <strong>Email</strong>, <strong>SMS</strong>, <strong>WhatsApp</strong>, or <strong>Survey Email</strong></p>
            <p>• Upload Excel file with column <strong>code</strong> containing voucher codes</p>
            <p>• Optional for Email/Survey Email: Include <strong>beneficiaryEmail</strong> column with valid email addresses</p>
            <p>• Optional for SMS: Include <strong>beneficiaryPhone</strong> column with valid 10-digit phone numbers</p>
            <p>• The system will send individual notifications to each recipient in the Excel file</p>
            <p>• Each notification will contain the voucher code for the recipient</p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Notification;
